import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { PayrollRevision } from "../../Common/Services/PayrollRevision";
import { reject } from "q";

export default function PayrollExportModal({
  show,
  closeModal,
  hotelList,
  onExport,
  selectedTenantID,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [selectedHotels, setSelectedHotels] = useState<string[]>([]);
  const selectAllRef = useRef<HTMLInputElement>(null); // Ref for Select All checkbox

  const filteredHotels = hotelList.filter(
    (hotel) =>
      hotel.letterCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hotel.hotelNameAbbr.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAllSelected =
    filteredHotels.length > 0 &&
    filteredHotels.every((hotel) => selectedHotels.includes(hotel.ehid));

  const isSomeSelected = filteredHotels.some((hotel) =>
    selectedHotels.includes(hotel.ehid)
  );

  // Update the indeterminate state of Select All checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = !isAllSelected && isSomeSelected;
    }
  }, [selectedHotels, filteredHotels]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = filteredHotels.map((hotel) => hotel.ehid);

      // Create a Set to avoid duplicates
      const mergedSelection = Array.from(
        new Set([...selectedHotels, ...newSelected])
      );

      setSelectedHotels(mergedSelection);
    } else {
      // Deselect only the filtered hotels, keep the rest if any
      const filteredEhidSet = new Set(
        filteredHotels.map((hotel) => hotel.ehid)
      );
      const remainingSelected = selectedHotels.filter(
        (ehid) => !filteredEhidSet.has(ehid)
      );

      setSelectedHotels(remainingSelected);
    }
  };

  const handleSelect = (ehid: string) => {
    setSelectedHotels((prevSelected) =>
      prevSelected.includes(ehid)
        ? prevSelected.filter((item) => item !== ehid)
        : [...prevSelected, ehid]
    );
  };

  const handleExport = () => {

    const hasRevision = hotelList.some(
      (hotel) => selectedHotels.includes(hotel.ehid) && hotel?.hasRevFile
    );
    if (hasRevision) {
      confirmAlert({
        title: "",
        message:
          "To export revision files, you must first turn off the Daily Revisions in the Management Portal. Then come here and try again.",
        buttons: [
          {
            label: "Close",
            onClick: () => reject(),
          },
        ],
        closeOnEscape: false,
        closeOnClickOutside: false,
        overlayClassName: "export-confirmation",
      });
    } else {
      const request = {
        selectedtenantID: selectedTenantID,
        HotelIds: selectedHotels,
      };

      setIsExporting(true);
      PayrollRevision.SavePayrollRevision(request)
        .then((result) => {
          if (result.message === "Success") {
            toast.success(result?.result);
          }
        })
        .catch((err) => {})
        .finally(() => {
          onExport();
          closeModal();
          setIsExporting(false);
        });
    }
  };

  return (
    <Modal
      show={show}
      onHide={closeModal}
      centered
      className="export-new-revisions-modal"
    >
      <Modal.Header>
        <Modal.Title>Export New Revisions</Modal.Title>
        <Button
          variant="link"
          onClick={closeModal}
          className="close p-0 ms-auto"
          style={{ fontSize: "1.5rem", fontWeight: "bold" }}
        >
          ×
        </Button>
      </Modal.Header>
      <Modal.Body>
        <label className="mb-2 field-title">Select EHIDs</label>
        <Form.Control
          type="text"
          placeholder="Filter Search"
          className="mb-3 search-box"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {searchTerm?.length > 0 && filteredHotels.length == 0 ? (
          <p>No EHIDs Found.</p>
        ) : (
          <></>
        )}

        {filteredHotels.length > 0 && (
          <Form.Check
            custom
            type="checkbox"
            id="select-all"
            label="Select All"
            checked={isAllSelected}
            onChange={handleSelectAll}
            ref={selectAllRef} // Attach ref to the checkbox
          />
        )}

        {filteredHotels.map((hotel) => (
          <Form.Check
            custom
            key={hotel.ehid}
            type="checkbox"
            id={hotel.letterCode}
            label={
              <span>
                <strong>{hotel.letterCode}</strong> {hotel.hotelNameAbbr}
              </span>
            }
            checked={selectedHotels.includes(hotel.ehid)}
            onChange={() => handleSelect(hotel.ehid)}
          />
        ))}
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button variant="light" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          disabled={selectedHotels?.length == 0 || isExporting}
          onClick={handleExport}
          variant="primary"
        >
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
